"use client";

import { useEffect, useState } from "react";
import { calculateExchange, request } from "@/lib/helpers";
import { decryptResponse } from "@/lib/crossborder-crypto";
import ENDPOINTS from "@/lib/endpoints";

export default function useConvertCalculation() {
  const [fromOpen, setFromOpen] = useState(false);
  const [toOpen, setToOpen] = useState(false);

  const [senderCountries, setSenderCountries] = useState([]);
  const [receiverCountries, setReceiverCountries] = useState([]);
  const [fromSelected, setFromSelected] = useState(null);
  const [toSelected, setToSelected] = useState(null);

  const [amount, setAmount] = useState("");
  const [recipientAmount, setRecipientAmount] = useState("");
  const [lastEdited, setLastEdited] = useState("amount");

  const [fees, setFees] = useState(0);
  const [conversionRate, setConversionRate] = useState(1);
  const [totalYouPay, setTotalYouPay] = useState(0);

  const [loading, setLoading] = useState(true);
  const [selectedFacility, setSelectedFacility] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const { data: response } = await request.get(`${ENDPOINTS.EXCHANGE_DATA}`);

        let exchangeData = null;

        if (response?.payload) {
          const decrypted = decryptResponse(response.payload);
          exchangeData = decrypted?.data || null;
        } else {
          exchangeData = response?.data || null;
        }

        setSenderCountries(exchangeData?.sender_countries || []);
        setReceiverCountries(exchangeData?.receiver_countries || []);
        // Set default sender country to UK (GBP) if available
        const gbpCountry = (exchangeData?.sender_countries || []).find(
          (country) => country.code === "GBP",
        );
        setFromSelected(
          gbpCountry || exchangeData?.sender_countries?.[0] || null,
        );
        const firstReceiverCountry = exchangeData?.receiver_countries?.[0] || null;
        setToSelected(firstReceiverCountry);
        // Set first facility from the first receiver country
        setSelectedFacility(firstReceiverCountry?.facilities?.[0] || null);
      } catch (err) {
        console.error("Exchange data fetch error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!fromSelected || !toSelected) return;

    const senderCountry = senderCountries?.find(
      (country) => country.id === fromSelected.id,
    );

    const { conversionRate, fees, totalYouPay } = calculateExchange({
      amount: parseFloat(amount) || 0,
      recipientAmount: parseFloat(recipientAmount) || 0,
      lastEdited,
      senderCountry: senderCountry || fromSelected,
      receiverCountry: toSelected,
      setAmount,
      setRecipientAmount,
      selectedFacility,
    });

    setConversionRate(conversionRate);
    // Only set fees if there's a selected facility
    setFees(selectedFacility ? fees : null);
    setTotalYouPay(totalYouPay);
  }, [
    amount,
    recipientAmount,
    fromSelected,
    toSelected,
    lastEdited,
    selectedFacility,
    senderCountries, // Added to dependencies
  ]);

  return {
    loading,
    fromOpen,
    toOpen,
    senderCountries,
    receiverCountries,
    fromSelected,
    toSelected,
    amount,
    recipientAmount,
    fees,
    conversionRate,
    totalYouPay,
    selectedFacility,
    setFromOpen,
    setToOpen,
    setFromSelected,
    setToSelected,
    setAmount,
    setRecipientAmount,
    setLastEdited,
    setSelectedFacility,
  };
}
